import { Session } from "@talkjs/react";
import { ErrorBoundary } from "@themes/pollinator/ui/ErrorBoundary";
import { TalkJSData } from "@core/features/chat";

export type Props = {
  talkjsData: TalkJSData | null | undefined;
  children: React.ReactNode;
};

/**
 * Surface a structured payload when the TalkJS session fails to mount.
 *
 * The fallback is intentionally graceful (renders children without
 * <Session>) so a TalkJS outage doesn't take the rest of the app down.
 * That graceful path used to be silent — same UX whether the user has
 * no conversations or whether the SDK actually broke. This logger keeps
 * the graceful behavior and adds enough breadcrumbs for ops to tell the
 * difference, including whether the failure happened on the dashboard
 * (a nuisance the user can refresh out of) or inside a live meeting
 * (where the user is committed to the call and can't trivially recover).
 */
function logChatSessionError(error: unknown) {
  const route =
    typeof window !== "undefined" ? window.location.pathname : "<ssr>";
  const isInMeeting = route.startsWith("/video/meeting/");
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  console.error("talkjs.session.init.failed", {
    message,
    stack,
    route,
    isInMeeting,
  });
}

export function ChatSession({ talkjsData, children }: Props) {
  if (!talkjsData) {
    return children;
  }

  if (!talkjsData.app_id || !talkjsData.user_id || !talkjsData.token) {
    return children;
  }

  return (
    <ErrorBoundary fallback={<>{children}</>} onError={logChatSessionError}>
      <Session
        appId={talkjsData.app_id}
        userId={talkjsData.user_id}
        token={talkjsData.token}
      >
        {children}
      </Session>
    </ErrorBoundary>
  );
}

export default ChatSession;


